import { FilterPanel } from "@components/FilterPanel";
import { Opportunities } from "@components/Opportunities";
import { RepSelector } from "@components/RepSelector";
import { RepTop30 } from "@components/RepTop30";
import { ResultsPanel } from "@components/ResultsPanel";
import { resetRepTop30, setSelectedRep } from "@app-redux/slices/repTop30Slice";
import {
  clearOpportunitySelection,
  setSelectedCategory,
  setSelectedCategoryAndSubcategory,
} from "@app-redux/slices/opportunitySlice";
import { updateFilterCategoriesArr } from "@app-redux/slices/filtersSlice";
import {
  filtersSelector,
  selectedRepSelector,
  selectedCategoryIdSelector,
  selectedSubcategorySelector,
} from "@app-redux/selectors";
import { useAppDispatch, useAppSelector } from "@app-redux/store";
import { useCategories, useOpportunityCategories, useSubcategoryCounts } from "@/hooks/queries";
import { useIsCompact } from "@/hooks/useResponsive";
import { useFilterDrawer } from "@/hooks/useFilterDrawer";
import type { OpportunityCategory } from "@api/opportunity";
import Badge from "@mui/material/Badge";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import Fab from "@mui/material/Fab";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterListIcon from "@mui/icons-material/FilterList";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import React, { useState, useCallback, useMemo } from "react";
import { useBranding } from "@/hooks/queries";
import styles from "./styles.module.css";

type ViewMode = "profiles" | "top30" | "opportunities";

export const Main = () => {
  const dispatch = useAppDispatch();
  const [viewMode, setViewMode] = useState<ViewMode>("profiles");
  const [showTouringChips, setShowTouringChips] = useState(false);
  const { data: categories } = useCategories();
  const { categories_values: selectedFilters = [] } =
    useAppSelector(filtersSelector);
  const selectedRep = useAppSelector(selectedRepSelector);
  const selectedCategoryId = useAppSelector(selectedCategoryIdSelector);
  const selectedSubcategory = useAppSelector(selectedSubcategorySelector);
  const { data: oppCategories = [] } = useOpportunityCategories();
  const { data: branding } = useBranding();
  const isCompact = useIsCompact();
  const { open: filterDrawerOpen, close: closeFilterDrawer, toggle: toggleFilterDrawer } = useFilterDrawer();
  const [categoryDrawerOpen, setCategoryDrawerOpen] = useState(false);
  const activeFilterCount = selectedFilters.length;

  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [openMenuCategoryId, setOpenMenuCategoryId] = useState<string | null>(null);
  const [expandedDrawerCategoryId, setExpandedDrawerCategoryId] = useState<string | null>(null);

  const selectedCategory = useMemo(() => {
    if (!selectedCategoryId) return null;
    return oppCategories.find((c) => c.id === selectedCategoryId) ?? null;
  }, [oppCategories, selectedCategoryId]);

  const sortedCategories = useMemo(
    () => [...oppCategories].sort((a, b) => a.sort_order - b.sort_order),
    [oppCategories]
  );

  const handleViewChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, newMode: ViewMode | null) => {
      if (!newMode || newMode === viewMode) return;

      if (viewMode === "opportunities") {
        dispatch(clearOpportunitySelection());
        setShowTouringChips(false);
      }

      if (viewMode === "top30") {
        if (selectedRep) {
          const isAlreadyFiltered = selectedFilters.includes(selectedRep.id);
          if (!isAlreadyFiltered) {
            dispatch(
              updateFilterCategoriesArr({
                id: selectedRep.id,
                checked: false,
              })
            );
          }
        }
        dispatch(resetRepTop30());
      }

      if (newMode === "top30") {
        const repCategory = categories.find((cat) => cat.name === "Rep");
        if (repCategory) {
          const repIds = new Set(repCategory.items.map((r) => r.id));
          const selectedRepId = selectedFilters.find((id) => repIds.has(id));
          if (selectedRepId) {
            const rep = repCategory.items.find((r) => r.id === selectedRepId);
            if (rep) {
              dispatch(setSelectedRep({ id: rep.id, name: rep.name }));
            }
          }
        }
      }

      setViewMode(newMode);
    },
    [viewMode, categories, selectedFilters, selectedRep, dispatch]
  );

  const handleTouringClick = useCallback(() => {
    if (viewMode === "opportunities") {
      dispatch(clearOpportunitySelection());
      setShowTouringChips(false);
      setViewMode("profiles");
    } else {
      if (viewMode === "top30") {
        if (selectedRep) {
          const isAlreadyFiltered = selectedFilters.includes(selectedRep.id);
          if (!isAlreadyFiltered) {
            dispatch(
              updateFilterCategoriesArr({
                id: selectedRep.id,
                checked: false,
              })
            );
          }
        }
        dispatch(resetRepTop30());
      }
      setShowTouringChips(true);
      setViewMode("opportunities");
    }
  }, [viewMode, selectedRep, selectedFilters, dispatch]);

  const handleCategoryClick = useCallback(
    (categoryId: string) => {
      if (viewMode === "opportunities" && selectedCategoryId === categoryId) {
        setCategoryDrawerOpen(false);
        return;
      }

      dispatch(setSelectedCategory(categoryId));
      setViewMode("opportunities");
      setCategoryDrawerOpen(false);
    },
    [viewMode, selectedCategoryId, dispatch]
  );

  const handleSubcategoryChipClick = useCallback(
    (event: React.MouseEvent<HTMLElement>, categoryId: string) => {
      setMenuAnchorEl(event.currentTarget);
      setOpenMenuCategoryId(categoryId);
    },
    []
  );

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
    setOpenMenuCategoryId(null);
  }, []);

  const handleSubcategorySelect = useCallback(
    (categoryId: string, subcategoryId: string) => {
      dispatch(setSelectedCategoryAndSubcategory({ categoryId, subcategory: subcategoryId }));
      setViewMode("opportunities");
      setMenuAnchorEl(null);
      setOpenMenuCategoryId(null);
      setCategoryDrawerOpen(false);
    },
    [dispatch]
  );

  const handleDrawerCategoryToggle = useCallback(
    (categoryId: string) => {
      setExpandedDrawerCategoryId(
        expandedDrawerCategoryId === categoryId ? null : categoryId
      );
    },
    [expandedDrawerCategoryId]
  );

  const compactChipLabel = useMemo(() => {
    if (!selectedCategory) return "";
    const hasSubcategories = (selectedCategory.subcategories?.length ?? 0) > 0;
    if (selectedSubcategory && hasSubcategories) {
      const subName = selectedCategory.subcategories.find((s) => s.id === selectedSubcategory)?.name;
      return subName ? `${selectedCategory.name} — ${subName}` : selectedCategory.name;
    }
    return selectedCategory.name;
  }, [selectedCategory, selectedSubcategory]);

  const sidePanel = viewMode === "profiles" ? <FilterPanel /> : <RepSelector />;

  return (
    <div className={styles.main}>
      <div className={`${styles.topBar} ${isCompact ? styles.topBarCompact : ""}`}>
        <ToggleButtonGroup
          value={viewMode !== "opportunities" ? viewMode : null}
          exclusive
          onChange={handleViewChange}
          size="small"
        >
          <ToggleButton value="profiles">All Profiles</ToggleButton>
          <ToggleButton value="top30">Rep Top 30</ToggleButton>
        </ToggleButtonGroup>
        <Divider orientation="vertical" flexItem />
        <ToggleButton
          value="touring"
          selected={showTouringChips || viewMode === "opportunities"}
          onChange={handleTouringClick}
          size="small"
        >
          {branding?.opportunities_label || "Touring and Show Listings"}
        </ToggleButton>
        {(showTouringChips || viewMode === "opportunities") && !isCompact && (
          <>
            <Divider orientation="vertical" flexItem />
            <div className={styles.categoryChips}>
              {sortedCategories.map((cat) => {
                const hasSubcategories = (cat.subcategories?.length ?? 0) > 0;
                const isSelected = selectedCategoryId === cat.id;
                if (hasSubcategories) {
                  return (
                    <CategoryWithSubcategoriesChip
                      key={cat.id}
                      category={cat}
                      isSelected={isSelected}
                      selectedSubcategory={isSelected ? selectedSubcategory : null}
                      menuOpen={openMenuCategoryId === cat.id}
                      anchorEl={openMenuCategoryId === cat.id ? menuAnchorEl : null}
                      onChipClick={handleSubcategoryChipClick}
                      onMenuClose={handleMenuClose}
                      onSubcategorySelect={handleSubcategorySelect}
                    />
                  );
                }
                return (
                  <Chip
                    key={cat.id}
                    label={cat.name}
                    variant={isSelected ? "filled" : "outlined"}
                    color={isSelected ? "primary" : "default"}
                    onClick={() => handleCategoryClick(cat.id)}
                    className={styles.categoryChip}
                    sx={isSelected ? {
                      backgroundColor: "primary.main",
                      color: "#fff",
                      fontWeight: 600,
                      "&:hover": { backgroundColor: "primary.dark" },
                    } : undefined}
                  />
                );
              })}
            </div>
          </>
        )}
      </div>
      {(showTouringChips || viewMode === "opportunities") && isCompact && compactChipLabel && (
        <div className={styles.categoryRow}>
          <Chip
            label={compactChipLabel}
            color="primary"
            deleteIcon={<ExpandMoreIcon />}
            onDelete={() => setCategoryDrawerOpen(true)}
            onClick={() => setCategoryDrawerOpen(true)}
            sx={{
              fontWeight: 600,
              "& .MuiChip-deleteIcon": { color: "inherit" },
            }}
          />
        </div>
      )}
      <div className={styles.contentArea}>
        {viewMode === "opportunities" ? (
          <Opportunities />
        ) : (
          <>
            {isCompact ? (
              <Drawer
                anchor="left"
                open={filterDrawerOpen}
                onClose={closeFilterDrawer}
                PaperProps={{ sx: { width: 320, maxWidth: "85vw" } }}
              >
                {sidePanel}
              </Drawer>
            ) : (
              <div className={styles.leftPanel}>
                {sidePanel}
              </div>
            )}
            {viewMode === "profiles" ? <ResultsPanel /> : <RepTop30 />}
          </>
        )}
      </div>
      {isCompact && viewMode !== "opportunities" && (
        <Fab
          color="primary"
          aria-label="Open filters"
          onClick={toggleFilterDrawer}
          sx={{
            position: "fixed",
            bottom: 24,
            right: 16,
            zIndex: 1100,
          }}
        >
          <Badge badgeContent={activeFilterCount} color="error" max={99}>
            <FilterListIcon />
          </Badge>
        </Fab>
      )}
      {isCompact && (
        <Drawer
          anchor="left"
          open={categoryDrawerOpen}
          onClose={() => setCategoryDrawerOpen(false)}
          PaperProps={{ sx: { width: 280, maxWidth: "80vw" } }}
        >
          <List
            subheader={
              <ListSubheader component="div" sx={{ fontWeight: 700, fontSize: 16 }}>
                Categories
              </ListSubheader>
            }
          >
            {sortedCategories.map((cat) => {
              const hasSubcategories = (cat.subcategories?.length ?? 0) > 0;
              if (hasSubcategories) {
                return (
                  <DrawerCategoryWithSubcategories
                    key={cat.id}
                    category={cat}
                    isSelected={selectedCategoryId === cat.id}
                    selectedSubcategory={selectedCategoryId === cat.id ? selectedSubcategory : null}
                    expanded={expandedDrawerCategoryId === cat.id}
                    onToggle={handleDrawerCategoryToggle}
                    onSubcategorySelect={handleSubcategorySelect}
                  />
                );
              }
              return (
                <ListItemButton
                  key={cat.id}
                  selected={selectedCategoryId === cat.id}
                  onClick={() => handleCategoryClick(cat.id)}
                >
                  <ListItemText primary={cat.name} />
                </ListItemButton>
              );
            })}
          </List>
        </Drawer>
      )}
    </div>
  );
};

type CategoryWithSubcategoriesChipProps = {
  category: OpportunityCategory;
  isSelected: boolean;
  selectedSubcategory: string | null;
  menuOpen: boolean;
  anchorEl: HTMLElement | null;
  onChipClick: (event: React.MouseEvent<HTMLElement>, categoryId: string) => void;
  onMenuClose: () => void;
  onSubcategorySelect: (categoryId: string, subcategoryId: string) => void;
};

const CategoryWithSubcategoriesChip = ({
  category,
  isSelected,
  selectedSubcategory,
  menuOpen,
  anchorEl,
  onChipClick,
  onMenuClose,
  onSubcategorySelect,
}: CategoryWithSubcategoriesChipProps) => {
  const { data: counts = {} } = useSubcategoryCounts(menuOpen || isSelected ? category.id : null);

  const selectedSubName = selectedSubcategory
    ? category.subcategories.find((s) => s.id === selectedSubcategory)?.name
    : null;

  const chipLabel = isSelected && selectedSubName
    ? `${category.name} — ${selectedSubName}`
    : category.name;

  return (
    <>
      <Chip
        label={chipLabel}
        variant={isSelected ? "filled" : "outlined"}
        color={isSelected ? "primary" : "default"}
        onClick={(e) => onChipClick(e, category.id)}
        deleteIcon={<ExpandMoreIcon />}
        onDelete={(e) => onChipClick(e as unknown as React.MouseEvent<HTMLElement>, category.id)}
        className={styles.categoryChip}
        sx={isSelected ? {
          backgroundColor: "primary.main",
          color: "#fff",
          fontWeight: 600,
          "&:hover": { backgroundColor: "primary.dark" },
          "& .MuiChip-deleteIcon": { color: "inherit" },
        } : {
          "& .MuiChip-deleteIcon": { color: "inherit" },
        }}
      />
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={onMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {category.subcategories.map((sub) => (
          <MenuItem
            key={sub.id}
            selected={isSelected && selectedSubcategory === sub.id}
            onClick={() => onSubcategorySelect(category.id, sub.id)}
            sx={{ justifyContent: "space-between", gap: 2 }}
          >
            <span>{sub.name}</span>
            <span style={{ color: "var(--gray-5, #666)", fontSize: 13 }}>
              {counts[sub.id] ?? 0}
            </span>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

type DrawerCategoryWithSubcategoriesProps = {
  category: OpportunityCategory;
  isSelected: boolean;
  selectedSubcategory: string | null;
  expanded: boolean;
  onToggle: (categoryId: string) => void;
  onSubcategorySelect: (categoryId: string, subcategoryId: string) => void;
};

const DrawerCategoryWithSubcategories = ({
  category,
  isSelected,
  selectedSubcategory,
  expanded,
  onToggle,
  onSubcategorySelect,
}: DrawerCategoryWithSubcategoriesProps) => {
  const { data: counts = {} } = useSubcategoryCounts(expanded || isSelected ? category.id : null);

  return (
    <>
      <ListItemButton onClick={() => onToggle(category.id)}>
        <ListItemText
          primary={category.name}
          primaryTypographyProps={isSelected ? { fontWeight: 600, color: "primary.main" } : undefined}
        />
        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </ListItemButton>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {category.subcategories.map((sub) => (
            <ListItemButton
              key={sub.id}
              sx={{ pl: 4 }}
              selected={isSelected && selectedSubcategory === sub.id}
              onClick={() => onSubcategorySelect(category.id, sub.id)}
            >
              <ListItemText
                primary={sub.name}
                secondary={`${counts[sub.id] ?? 0}`}
              />
            </ListItemButton>
          ))}
        </List>
      </Collapse>
    </>
  );
};
